// R116 #7 (Kai, MTR Pipeline: "moving a lead to Touring … create a Task for the
// tour, placed on the available turnover from our HA calendar") — the Pipeline's
// tour UI, kept out of MtrPage.ts (which only hooks it in):
//   • the Schedule-tour SHEET — opened when a lead is moved into Touring with no
//     tour booked (caught BEFORE the move is sent), from a card's "No tour yet"
//     chip, the drawer's "＋ Schedule tour" and "Reschedule…". Suggested days come
//     from GET /api/mtr/leads/:id/tour-slots (turnover days first, then empty
//     days — never a night with a guest inside); a hand-picked day is checked
//     and warns (never blocks). "Schedule tour" on a move = ONE write
//     (POST /tours {advance:true}); "Just move" = the plain move (Undo toast kept).
//   • the drawer's TOURS section — each tour with its task (who has it, its
//     status) and, when the task's due day was changed in Tasks, "dates differ"
//     with a one-tap "Move tour to <date>" (never a silent overwrite).
//   • the card chip — the next tour, or "No tour yet · Schedule" on a Touring card.
//   • the drawer's home change — asks whether its scheduled tours move too.
// Design: the Calendar stay-panel idiom (soft cards, small uppercase labels with
// count chips, one blue primary, pills, muted helper text, the rest behind ⋯);
// :root tokens only; phone-first (bottom sheet ≤ 640 px, 36 px targets).
import { kebabHtml, bindKebabs, type KebabItem } from '../../shared/components/Kebab';

export interface TourHost {
  container: HTMLElement;
  lead(id: string | null): any;
  properties(): Array<{ listing_id: string; name: string }>;
  listingName(id: string | null): string;
  people(): Array<{ email: string; full_name: string }>;
  loadPeople(): Promise<void>;
  leads(): any[];
  send(method: 'POST' | 'PUT' | 'DELETE' | 'GET', path: string, body?: any): Promise<{ ok: boolean; status: number; j: any }>;
  applyLead(view: any): void;          // replace the lead, redraw the board, repaint the drawer
  reloadLeads(): Promise<void>;        // after a 409 — fresh board + drawer
  toast(html: string): void;
  justMove(id: string, from: string): void;   // the plain move into Touring (skips this catch)
}

const ADVANCE_FROM = ['new', 'contacted', 'qualified'];
const OPEN_TASK = ['todo', 'in_progress', 'blocked'];
const AT_RX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STATUS_LABEL: Record<string, string> = { scheduled: 'Scheduled', done: 'Done', no_show: 'No-show', cancelled: 'Cancelled' };
const TASK_LABEL: Record<string, string> = { todo: 'To do', in_progress: 'In progress', blocked: 'Blocked', done: 'Done', canceled: 'Cancelled' };

const esc = (s: any) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const localToday = () => { try { return new Date().toLocaleDateString('en-CA'); } catch { return new Date().toISOString().slice(0, 10); } };
const nowLocal = () => { const d = new Date(); const p = (n: number) => String(n).padStart(2, '0'); return `${localToday()}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const addDays = (d: string, n: number) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
export function dayLabel(d: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(d || ''))) return String(d || '');
  const x = new Date(`${d}T00:00:00Z`);
  return `${WDAY[x.getUTCDay()]} ${MON[x.getUTCMonth()]} ${x.getUTCDate()}`;
}
export function timeLabel(hm: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(String(hm || ''));
  if (!m) return String(hm || '');
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}
export function whenLabel(at: string): string {
  const m = AT_RX.exec(String(at || ''));
  return m ? `${dayLabel(at.slice(0, 10))} · ${timeLabel(at.slice(11, 16))}` : String(at || '');
}
const firstName = (name: any) => String(name || '').trim().split(/\s+/)[0] || 'the renter';
const upcoming = (t: any) => t && t.status === 'scheduled' && String(t.at || '') >= nowLocal();
export function nextTour(l: any): any {
  const ts = (Array.isArray(l?.tours) ? l.tours : []).filter(upcoming).sort((a: any, b: any) => (a.at < b.at ? -1 : 1));
  return ts[0] || null;
}

type SheetMode = 'move' | 'add' | 'edit';
interface SheetOpts { mode: SheetMode; leadId: string; from?: string; tour?: any; focusJustMove?: boolean }
interface SheetState extends SheetOpts {
  listing: string | null; date: string; time: string; with: string; note: string; advance: boolean;
  slots: any[] | null; slotsErr: string; payload: any; picked: string | null; warnings: any[]; checking: boolean; busy: boolean; err: string;
}

export class PipelineTours {
  private h: TourHost;
  private sheet: SheetState | null = null;
  private sheetEl: HTMLElement | null = null;
  private escOff: (() => void) | null = null;
  private checkTimer: ReturnType<typeof setTimeout> | null = null;
  private checkSeq = 0;
  private homeEdit: { leadId: string; value: string; follow: boolean; busy: boolean } | null = null;
  private askDelete: string | null = null;

  constructor(host: TourHost) { this.h = host; }

  isOpen(): boolean { return !!this.sheet; }
  hasUpcoming(l: any): boolean { return !!nextTour(l); }
  /** Was a drawer/card action ours? (bindDrawerHost's delegated click asks first.) */
  editingHome(): boolean { return !!this.homeEdit; }

  // ── board card chip ─────────────────────────────────────────────────────
  cardChipHtml(l: any): string {
    const nt = nextTour(l);
    if (nt) {
      const diff = !!(nt.task && nt.task.dates_differ);
      return `<div class="mtt-cardchip${diff ? ' warn' : ''}" title="Next tour${nt.with ? ` with ${esc(nt.with)}` : ''}${diff ? ' — dates differ: its task is due on another day (open the card to line them up)' : ''}">🗓 ${esc(whenLabel(nt.at))}${diff ? ' <span aria-label="dates differ">⚠</span>' : ''}</div>`;
    }
    if (l.stage === 'touring') return `<button type="button" class="mtt-cardchip empty" data-mtt-sched="${esc(l.id)}" title="This lead is in Touring with no tour booked — schedule it">🗓 No tour yet · Schedule</button>`;
    return '';
  }

  // ── the drawer's Tours section ──────────────────────────────────────────
  sectionHtml(l: any): string {
    const tours: any[] = (Array.isArray(l.tours) ? l.tours : []).slice()
      .sort((a, b) => (upcoming(a) !== upcoming(b) ? (upcoming(a) ? -1 : 1) : upcoming(a) ? (a.at < b.at ? -1 : 1) : (a.at < b.at ? 1 : -1)));
    const settled = ['signed', 'active', 'lost', 'ended'].includes(l.stage);
    if (!tours.length && settled) return '';
    const none = !tours.some(upcoming);
    const primary = none && l.stage === 'touring';
    return `<section class="mtr-dr-sec mtt-sec" data-mtt-sec>
      <h4>Tours <span class="cnt">${tours.length}</span></h4>
      ${primary ? `<div class="mtt-note">In Touring with no tour booked — schedule it so whoever shows the home gets the task.</div>` : ''}
      ${tours.length ? `<div class="mtt-tours">${tours.map(t => this.tourRowHtml(l, t)).join('')}</div>` : (primary ? '' : '<div class="mtr-dr-empty">No tours yet — scheduling one makes the task for whoever shows the home.</div>')}
      <div class="mtr-dr-row mtt-addrow"><button type="button" class="btn ${primary ? 'btn-primary mtt-wide' : 'btn-secondary'} btn-sm" data-mtt-act="sched">＋ Schedule tour</button></div>
    </section>`;
  }

  private personName(email: string): string {
    const p = this.h.people().find(x => x.email.toLowerCase() === String(email || '').toLowerCase());
    return p && p.full_name && !p.full_name.includes('@') ? p.full_name.split(/\s+/)[0] : String(email || '').split('@')[0];
  }

  private tourRowHtml(l: any, t: any): string {
    const sch = t.status === 'scheduled';
    const past = sch && !upcoming(t);
    const home = t.listing_id && String(t.listing_id) !== String(l.property_interest || '') ? this.h.listingName(t.listing_id) : '';
    const task = t.task;
    const tid = esc(t.id);
    const pill = `<span class="mtt-pill ${t.status === 'done' ? 'ok' : sch ? (past ? 'warn' : 'info') : 'muted'}">${esc(past ? 'Time passed' : STATUS_LABEL[t.status] || t.status)}</span>`;
    const taskLine = task
      ? `<button type="button" class="mtt-task" data-mtt-act="opentask" data-task="${esc(task.id)}" title="Open the task in Tasks">📋 Task · ${esc((task.assignees || []).map((e: string) => this.personName(e)).join(', ') || 'unassigned')} · ${esc(TASK_LABEL[task.status] || task.status)}${task.due_time && task.due_date ? '' : ''} <span aria-hidden="true">›</span></button>`
      : (t.task === null && sch && !past ? '<span class="mtt-task none">📋 No task yet — it is created on the next save or by the 4-hourly repair</span>' : '');
    let differ = '';
    if (task && task.dates_differ && sch) {
      const to = `${task.due_date}T${task.due_time || String(t.at).slice(11, 16)}`;
      differ = `<div class="mtt-differ" role="note">
        <div>⚠ <b>Dates differ</b> — the task is due ${esc(dayLabel(task.due_date))}, the tour is still ${esc(dayLabel(String(t.at).slice(0, 10)))}.</div>
        <div class="mtt-differ-acts">
          <button type="button" class="btn btn-secondary btn-sm" data-mtt-act="movetour" data-tour="${tid}" data-at="${esc(to)}">Move tour to ${esc(dayLabel(task.due_date))}</button>
          <button type="button" class="btn btn-ghost btn-sm" data-mtt-act="taskback" data-tour="${tid}" data-task="${esc(task.id)}">Set the task back</button>
        </div>
      </div>`;
    }
    const more: KebabItem[] = [
      { icon: '🗓', label: 'Reschedule…', attrs: { 'data-mtt-k': 'resched', 'data-tour': t.id } },
      sch ? { icon: '⊘', label: 'Cancel tour', attrs: { 'data-mtt-k': 'cancelled', 'data-tour': t.id } } : { icon: '↺', label: 'Mark scheduled again', attrs: { 'data-mtt-k': 'scheduled', 'data-tour': t.id } },
      { divider: true },
      { icon: '🗑', label: 'Delete tour', danger: true, attrs: { 'data-mtt-k': 'delete', 'data-tour': t.id } },
    ];
    const asking = this.askDelete === t.id;
    return `<div class="mtt-tour ${esc(t.status)}" data-mtt-tour="${tid}">
      <div class="mtt-tour-h">
        <div class="mtt-when">${esc(whenLabel(t.at))}</div>
        ${pill}
        ${kebabHtml(more, { tip: 'Tour actions' })}
      </div>
      ${home || t.with ? `<div class="mtt-sub">${home ? `${esc(home)}${t.with ? ' · ' : ''}` : ''}${t.with ? `with ${esc(t.with)}` : ''}</div>` : ''}
      ${t.note ? `<div class="mtt-sub muted" title="Office-only — not copied to the task">📝 ${esc(t.note)}</div>` : ''}
      ${taskLine ? `<div class="mtt-taskrow">${taskLine}</div>` : ''}
      ${differ}
      ${sch ? `<div class="mtt-tour-acts"><button type="button" class="btn btn-secondary btn-sm" data-mtt-act="done" data-tour="${tid}">✓ Done</button><button type="button" class="btn btn-ghost btn-sm" data-mtt-act="no_show" data-tour="${tid}">No-show</button></div>` : ''}
      ${asking ? `<div class="mtt-ask">Delete this tour? Its task is cancelled; the history keeps a “tour deleted” line.<span class="mtt-ask-acts"><button type="button" class="btn btn-danger btn-sm" data-mtt-act="delete-yes" data-tour="${tid}">Delete</button><button type="button" class="btn btn-ghost btn-sm" data-mtt-act="delete-no">Keep</button></span></div>` : ''}
    </div>`;
  }

  /** Per-element binding for the ⋯ items (the Kebab moves an open menu to <body>). */
  bindDrawer(host: HTMLElement, l: any): void {
    bindKebabs(host);
    host.querySelectorAll<HTMLElement>('[data-mtt-k]').forEach(el => el.addEventListener('click', () => {
      const tour = (l.tours || []).find((x: any) => x.id === el.dataset.tour);
      if (!tour) return;
      const k = el.dataset.mttK;
      setTimeout(() => {   // after the ⋯ menu closed
        if (k === 'resched') this.openSheet({ mode: 'edit', leadId: l.id, tour });
        else if (k === 'delete') { this.askDelete = tour.id; this.h.applyLead(null); }
        else if (k === 'cancelled' || k === 'scheduled') void this.setStatus(l.id, tour.id, k);
      }, 0);
    }));
  }

  /** The drawer host's delegated click — true when it was a tour action. */
  handleDrawerClick(t: HTMLElement, leadId: string): boolean {
    const el = t.closest<HTMLElement>('[data-mtt-act], [data-mtt-home]');
    if (!el) return false;
    if ((el as HTMLButtonElement).disabled) return true;
    const l = this.h.lead(leadId);
    if (!l) return true;
    const act = el.dataset.mttAct || '';
    const tour = (l.tours || []).find((x: any) => x.id === el.dataset.tour) || null;
    if (el.dataset.mttHome) { this.homeAction(el.dataset.mttHome, l); return true; }
    switch (act) {
      case 'sched': this.openSheet({ mode: 'add', leadId }); break;
      case 'done': case 'no_show': if (tour) void this.setStatus(leadId, tour.id, act); break;
      case 'delete-yes': if (tour) void this.deleteTour(leadId, tour.id); break;
      case 'delete-no': this.askDelete = null; this.h.applyLead(null); break;
      case 'movetour': if (tour && el.dataset.at) void this.moveTourTo(leadId, tour, el.dataset.at); break;
      case 'taskback': if (tour && el.dataset.task) void this.taskBack(leadId, tour, el.dataset.task); break;
      case 'opentask': if (el.dataset.task) this.openTask(el.dataset.task); break;
    }
    return true;
  }

  private openTask(id: string): void {
    try { sessionStorage.setItem('tk_focus_task', id); } catch { /* private mode — Tasks opens on its list */ }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'tasks' }));
  }
  private async setStatus(leadId: string, tourId: string, status: string): Promise<void> {
    const r = await this.h.send('PUT', `/leads/${encodeURIComponent(leadId)}/tours/${encodeURIComponent(tourId)}`, { status });
    if (!r.ok) { alert(r.j.error || `Couldn’t update the tour (${r.status || 'network'}).`); return; }
    this.h.applyLead(r.j.lead);
    this.h.toast(`Tour marked <b>${esc(STATUS_LABEL[status] || status)}</b>${r.j.task ? ' · its task follows' : ''}${r.j.task_error ? ' · the task will catch up' : ''}`);
  }
  private async deleteTour(leadId: string, tourId: string): Promise<void> {
    this.askDelete = null;
    const r = await this.h.send('DELETE', `/leads/${encodeURIComponent(leadId)}/tours/${encodeURIComponent(tourId)}`);
    if (!r.ok) { alert(r.j.error || `Couldn’t delete the tour (${r.status || 'network'}).`); this.h.applyLead(null); return; }
    this.h.applyLead(r.j.lead);
    this.h.toast('Tour deleted · its task is cancelled');
  }
  private async moveTourTo(leadId: string, tour: any, at: string): Promise<void> {
    const r = await this.h.send('PUT', `/leads/${encodeURIComponent(leadId)}/tours/${encodeURIComponent(tour.id)}`, { at });
    if (!r.ok) { alert(r.j.error || `Couldn’t move the tour (${r.status || 'network'}).`); return; }
    this.h.applyLead(r.j.lead);
    this.h.toast(`Tour moved to <b>${esc(whenLabel(at))}</b> · the task matches again${r.j.warnings && r.j.warnings.length ? ` · ⚠ ${esc(r.j.warnings[0].text)}` : ''}`);
  }
  private async taskBack(leadId: string, tour: any, taskId: string): Promise<void> {
    try {
      const res = await fetch('/api/tasks', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: taskId, due_date: String(tour.at).slice(0, 10), due_time: String(tour.at).slice(11, 16) }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { alert(j.error || `Couldn’t change the task (${res.status}).`); return; }
      document.dispatchEvent(new CustomEvent('tasks:changed'));
    } catch { alert('Network error — the task was not changed.'); return; }
    await this.h.reloadLeads();
    this.h.toast(`Task set back to <b>${esc(whenLabel(tour.at))}</b>`);
  }

  // ── the lead's home (drawer Details) — asks whether scheduled tours move ─
  homeCellHtml(l: any): string {
    const ed = this.homeEdit && this.homeEdit.leadId === l.id ? this.homeEdit : null;
    if (!ed) return `${esc(this.h.listingName(l.property_interest))} <button type="button" class="mtt-linkbtn" data-mtt-home="edit">Change</button>`;
    const props = this.h.properties();
    const sched = (l.tours || []).filter((t: any) => upcoming(t) && !t.listing_id).length;
    const changing = ed.value !== String(l.property_interest || '');
    return `<div class="mtt-homeedit">
      <select class="mtt-input" data-mtt-home-sel aria-label="Home">${props.map(p => `<option value="${esc(p.listing_id)}"${p.listing_id === ed.value ? ' selected' : ''}>${esc(p.name)}</option>`).join('')}</select>
      ${sched && l.property_interest && changing ? `<div class="mtt-homeq">
        <div class="mtt-k">${sched} scheduled tour${sched === 1 ? '' : 's'} at ${esc(this.h.listingName(l.property_interest))}</div>
        <label class="mtt-radio"><input type="radio" name="mtt-follow" value="keep" ${ed.follow ? '' : 'checked'}> Keep ${sched === 1 ? 'it' : 'them'} at ${esc(this.h.listingName(l.property_interest))}</label>
        <label class="mtt-radio"><input type="radio" name="mtt-follow" value="move" ${ed.follow ? 'checked' : ''}> Move ${sched === 1 ? 'it' : 'them'} to the new home (the task${sched === 1 ? '' : 's'} follow${sched === 1 ? 's' : ''})</label>
      </div>` : ''}
      <div class="mtr-dr-row"><button type="button" class="btn btn-ghost btn-sm" data-mtt-home="cancel">Cancel</button><button type="button" class="btn btn-primary btn-sm" data-mtt-home="save" ${ed.busy || !changing ? 'disabled' : ''}>${ed.busy ? 'Saving…' : 'Save home'}</button></div>
    </div>`;
  }
  /** input/change inside the drawer (home select / radios). */
  handleDrawerInput(t: HTMLElement): boolean {
    if (!this.homeEdit) return false;
    if (t.matches('[data-mtt-home-sel]')) { this.homeEdit.value = (t as HTMLSelectElement).value; this.h.applyLead(null); return true; }
    if (t.matches('input[name="mtt-follow"]')) { this.homeEdit.follow = (t as HTMLInputElement).value === 'move'; return true; }
    return false;
  }
  private homeAction(what: string, l: any): void {
    if (what === 'edit') { this.homeEdit = { leadId: l.id, value: String(l.property_interest || (this.h.properties()[0] || { listing_id: '' }).listing_id), follow: false, busy: false }; this.h.applyLead(null); return; }
    if (what === 'cancel') { this.homeEdit = null; this.h.applyLead(null); return; }
    if (what === 'save' && this.homeEdit && !this.homeEdit.busy) void this.saveHome(l);
  }
  private async saveHome(l: any): Promise<void> {
    const ed = this.homeEdit!;
    ed.busy = true; this.h.applyLead(null);
    const r = await this.h.send('POST', `/leads/${encodeURIComponent(l.id)}`, { property_interest: ed.value, tours_follow: ed.follow });
    ed.busy = false;
    if (!r.ok) { alert(r.j.error || `Couldn’t change the home (${r.status || 'network'}).`); this.h.applyLead(null); return; }
    this.homeEdit = null;
    this.h.applyLead(r.j);
    this.h.toast(`Home changed to <b>${esc(this.h.listingName(ed.value))}</b>${ed.follow ? ' · tours moved with it' : ''}`);
  }
  resetForLead(id: string | null): void {
    if (this.homeEdit && this.homeEdit.leadId !== id) this.homeEdit = null;
    if (!id) this.askDelete = null;
  }

  // ── the Schedule-tour sheet ─────────────────────────────────────────────
  openSheet(o: SheetOpts): void {
    const l = this.h.lead(o.leadId);
    if (!l) return;
    this.closeSheet();
    const t = o.tour || null;
    const home = t ? (t.listing_id || l.property_interest || null) : (l.property_interest || null);
    this.sheet = {
      ...o,
      listing: home ? String(home) : null,
      date: t ? String(t.at).slice(0, 10) : '',
      time: t ? String(t.at).slice(11, 16) : '',
      with: t ? String(t.with || '') : '',
      note: t ? String(t.note || '') : '',
      advance: o.mode === 'add' ? ADVANCE_FROM.includes(l.stage) : false,
      slots: null, slotsErr: '', payload: null, picked: null, warnings: [], checking: false, busy: false, err: '',
    };
    const host = document.createElement('div');
    host.className = 'mtt-host';
    this.h.container.appendChild(host);
    this.sheetEl = host;
    this.paint(true);
    this.attachEsc();
    void this.h.loadPeople().then(() => { if (this.sheet) this.paintPeople(); });
    void this.loadSlots();
    setTimeout(() => {
      const want = this.sheetEl?.querySelector<HTMLElement>(o.focusJustMove ? '[data-mtt="just"]' : '[data-mtt-x]');
      want?.focus();
    }, 30);
  }
  closeSheet(): void {
    if (this.checkTimer) { clearTimeout(this.checkTimer); this.checkTimer = null; }
    this.sheet = null;
    if (this.sheetEl) { this.sheetEl.remove(); this.sheetEl = null; }
    this.escOff?.();
  }
  private attachEsc(): void {
    if (this.escOff) return;
    // capture on window: runs before the drawer's document listener, so Esc closes the sheet only
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !this.sheet) return;
      e.preventDefault(); e.stopPropagation();
      if (!this.sheet.busy) this.closeSheet();
    };
    window.addEventListener('keydown', onKey, true);
    this.escOff = () => { window.removeEventListener('keydown', onKey, true); this.escOff = null; };
  }

  private async loadSlots(): Promise<void> {
    const s = this.sheet;
    if (!s) return;
    s.slots = null; s.slotsErr = ''; s.payload = null;
    this.paintSlots();
    if (!s.listing) { s.slots = []; s.payload = { none: true, note: 'Pick the home first — its calendar decides the open days.' }; this.paintSlots(); return; }
    const want = s.listing;
    const r = await this.h.send('GET', `/leads/${encodeURIComponent(s.leadId)}/tour-slots?listing_id=${encodeURIComponent(want)}`);
    if (this.sheet !== s || s.listing !== want) return;
    if (!r.ok) { s.slots = []; s.slotsErr = r.j.error || 'Couldn’t read the home’s calendar — pick a day below.'; this.paintSlots(); return; }
    s.payload = r.j;
    s.slots = Array.isArray(r.j.slots) ? r.j.slots : [];
    // a new tour starts on the best suggestion; a reschedule keeps its own time
    if (s.mode !== 'edit' && !s.date && s.slots.length) { s.date = s.slots[0].date; s.time = s.slots[0].time; s.picked = s.slots[0].at; }
    else if (s.mode !== 'edit' && !s.date) { s.date = addDays(localToday(), 1); s.time = '11:00'; this.queueCheck(); }
    else if (s.date) { s.picked = s.slots.some(x => x.at === `${s.date}T${s.time}`) ? `${s.date}T${s.time}` : null; if (!s.picked) this.queueCheck(); }
    this.paint(false);
  }
  private queueCheck(): void {
    const s = this.sheet;
    if (!s) return;
    if (this.checkTimer) clearTimeout(this.checkTimer);
    s.warnings = [];
    if (!s.listing || !s.date || !s.time) { this.paintWarn(); return; }
    if (s.picked === `${s.date}T${s.time}`) { this.paintWarn(); return; }   // a suggested slot is clean by definition
    s.checking = true; this.paintWarn();
    const seq = ++this.checkSeq;
    this.checkTimer = setTimeout(async () => {
      const at = `${s.date}T${s.time}`;
      const r = await this.h.send('GET', `/leads/${encodeURIComponent(s.leadId)}/tour-slots?listing_id=${encodeURIComponent(s.listing || '')}&at=${encodeURIComponent(at)}`);
      if (this.sheet !== s || seq !== this.checkSeq) return;
      s.checking = false;
      s.warnings = r.ok && r.j.check ? (r.j.check.warnings || []) : [];
      this.paintWarn();
    }, 320);
  }

  // others' tours at this home that day (the client knows the whole board)
  private alsoOn(date: string, listing: string | null, selfLeadId: string, selfTourId?: string): string[] {
    if (!listing) return [];
    const out: string[] = [];
    for (const l of this.h.leads()) {
      if (!l || ['lost', 'ended'].includes(l.stage)) continue;
      for (const t of (l.tours || [])) {
        if (t.status !== 'scheduled' || String(t.at).slice(0, 10) !== date || t.id === selfTourId) continue;
        if (String(t.listing_id || l.property_interest || '') !== listing) continue;
        if (l.id === selfLeadId && !selfTourId) continue;
        out.push(`${firstName(l.name)} ${timeLabel(String(t.at).slice(11, 16))}`);
      }
    }
    return out;
  }

  private sheetTitle(): string {
    const s = this.sheet!;
    return s.mode === 'move' ? 'Schedule the tour?' : s.mode === 'edit' ? 'Reschedule the tour' : 'Schedule a tour';
  }
  private paint(fresh: boolean): void {
    const s = this.sheet, host = this.sheetEl;
    if (!s || !host) return;
    const l = this.h.lead(s.leadId);
    if (!l) { this.closeSheet(); return; }
    const props = this.h.properties();
    const homeOpts = `${s.listing ? '' : '<option value="" selected>— pick the home —</option>'}${props.map(p => `<option value="${esc(p.listing_id)}"${p.listing_id === s.listing ? ' selected' : ''}>${esc(p.name)}</option>`).join('')}`;
    const lede = s.mode === 'move'
      ? `Touring means a tour is booked. Pick a day and ${esc(firstName(l.name))} moves to Touring with the tour — whoever shows the home gets a task.`
      : s.mode === 'edit' ? 'Pick the new day — the task moves with the tour.' : 'Whoever shows the home gets a task for it.';
    const saveLabel = s.busy ? 'Saving…' : s.mode === 'edit' ? 'Save new time' : 'Schedule tour';
    host.innerHTML = `
      <div class="mtt-ov${fresh ? ' enter' : ''}" data-mtt-sheet>
        <div class="mtt-sheet" role="dialog" aria-modal="true" aria-labelledby="mtt-title">
          <header class="mtt-head">
            <div class="mtt-eyebrow"><span class="mtt-tag">${s.mode === 'edit' ? 'Reschedule' : 'Tour'}</span><span class="mtt-meta">${esc(l.name)} · ${esc(s.listing ? this.h.listingName(s.listing) : 'no home yet')}</span><button type="button" class="mtt-x" data-mtt-x aria-label="Close" title="Close (Esc)">✕</button></div>
            <h3 class="mtt-title" id="mtt-title">${esc(this.sheetTitle())}</h3>
            <p class="mtt-lede">${lede}</p>
          </header>
          <div class="mtt-body">
            <div data-mtt-banner></div>
            <section class="mtt-card">
              <div class="mtt-label">Suggested days <span class="mtt-cnt" data-mtt-cnt>…</span></div>
              <div class="mtt-slots" data-mtt-slots></div>
              <div class="mtt-basis" data-mtt-basis></div>
            </section>
            <section class="mtt-card">
              <div class="mtt-label">When</div>
              <div class="mtt-two">
                <label class="mtt-f">Day<input class="mtt-input" type="date" data-mtt-in="date" value="${esc(s.date)}" min="${esc(localToday())}"></label>
                <label class="mtt-f">Time<input class="mtt-input" type="time" step="900" data-mtt-in="time" value="${esc(s.time)}"></label>
              </div>
              <div data-mtt-warn></div>
            </section>
            <section class="mtt-card">
              <label class="mtt-f">Home<select class="mtt-input" data-mtt-in="listing">${homeOpts}</select></label>
              <label class="mtt-f">With — who shows it<input class="mtt-input" data-mtt-in="with" list="mtt-people" autocomplete="off" value="${esc(s.with)}" placeholder="a team member, or leave blank"><datalist id="mtt-people"></datalist>
                <span class="mtt-hint">A team login gets the task (you watch it). Anyone else — or blank — the task is yours to hand off.</span></label>
              <label class="mtt-f">Note <span class="mtt-hint-inline">office only</span><input class="mtt-input" data-mtt-in="note" autocomplete="off" maxlength="500" value="${esc(s.note)}" placeholder="optional — parking, who to call…">
                <span class="mtt-hint">Stays on the lead — never copied to the task (field staff can read tasks).</span></label>
              ${s.mode === 'add' && ADVANCE_FROM.includes(l.stage) ? `<label class="mtt-check"><input type="checkbox" data-mtt-in="advance" ${s.advance ? 'checked' : ''}> Move ${esc(firstName(l.name))} to <b>Touring</b></label>` : ''}
            </section>
            <div class="mtt-err" data-mtt-err ${s.err ? '' : 'hidden'}>${esc(s.err)}</div>
          </div>
          <footer class="mtt-foot${s.mode === 'move' ? ' two' : ''}" data-bottom-bar>
            ${s.mode === 'move' ? `<button type="button" class="btn btn-secondary" data-mtt="just" ${s.busy ? 'disabled' : ''} title="Move to Touring without booking a tour">Just move</button>` : `<button type="button" class="btn btn-ghost" data-mtt="cancel" ${s.busy ? 'disabled' : ''}>Cancel</button>`}
            <button type="button" class="btn btn-primary" data-mtt="save" ${s.busy ? 'disabled' : ''}>${saveLabel}</button>
          </footer>
        </div>
      </div>`;
    this.bindSheet();
    this.paintPeople();
    this.paintSlots();
    this.paintWarn();
  }
  private paintPeople(): void {
    const dl = this.sheetEl?.querySelector('#mtt-people');
    if (dl) dl.innerHTML = this.h.people().map(p => `<option value="${esc(p.full_name.includes('@') ? p.full_name.split('@')[0] : p.full_name)}"></option>`).join('');
  }
  private paintSlots(): void {
    const s = this.sheet, host = this.sheetEl;
    if (!s || !host) return;
    const box = host.querySelector<HTMLElement>('[data-mtt-slots]');
    const cnt = host.querySelector<HTMLElement>('[data-mtt-cnt]');
    const basis = host.querySelector<HTMLElement>('[data-mtt-basis]');
    const banner = host.querySelector<HTMLElement>('[data-mtt-banner]');
    if (!box || !cnt || !basis || !banner) return;
    const l = this.h.lead(s.leadId);
    const p = s.payload;
    // the move-in banner (booked / no day left) — and the virtual-tour copy
    const mib = p && p.move_in_booked;
    banner.innerHTML = mib
      ? `<div class="mtt-banner warn">🏠 <b>Their move-in (${esc(dayLabel(l && l.move_in))}) is already booked</b> — ${esc(String(mib.text || '').replace(/^A guest/, 'a guest').replace(/^The owner/, 'the owner').replace(/^An MTR/, 'an MTR'))}. Offer another start date${p.none ? ', or a virtual tour (video walk-through)' : ' — or a virtual tour if they can’t come before'}.</div>`
      : (p && p.bound === 'move_in' && p.none ? `<div class="mtt-banner warn">🏠 ${esc(p.note || '')}</div>` : '');
    if (s.slots === null) {
      cnt.textContent = '…';
      box.innerHTML = '<div class="mtt-skel"></div><div class="mtt-skel"></div><div class="mtt-skel"></div>';
      basis.textContent = 'Reading this home’s calendar…';
      return;
    }
    cnt.textContent = String(s.slots.length);
    if (!s.slots.length) {
      box.innerHTML = `<div class="mtt-empty">${esc(s.slotsErr || (p && p.note) || 'No open day found — pick one below.')}${p && p.next_turn && !s.slotsErr ? `<div class="mtt-empty-sub">Next checkout: ${esc(dayLabel(p.next_turn))}</div>` : ''}</div>`;
      basis.textContent = '';
      return;
    }
    box.innerHTML = s.slots.map((x: any) => {
      const on = s.picked === x.at;
      const also = this.alsoOn(x.date, s.listing, s.leadId, s.tour && s.tour.id);
      const turn = x.turn ? ` · turn ${esc(String(x.turn.status || 'not started').replace(/_/g, ' '))}${x.turn.cleaner_set ? ', cleaner set' : ''}` : '';
      return `<button type="button" class="mtt-slot${on ? ' on' : ''}" data-mtt-slot="${esc(x.at)}" aria-pressed="${on ? 'true' : 'false'}">
        <span class="mtt-slot-when"><span class="d">${esc(x.day_label || dayLabel(x.date))}</span><span class="t">${esc(x.time_label || timeLabel(x.time))}</span></span>
        <span class="mtt-slot-body"><span class="mtt-kind k-${esc(x.kind)}">${esc(x.label)}</span><span class="mtt-why">${esc(x.why)}${turn}</span>${also.length ? `<span class="mtt-also">Also touring: ${esc(also.join(', '))}</span>` : ''}</span>
        <span class="mtt-slot-tick" aria-hidden="true">${on ? '✓' : ''}</span>
      </button>`;
    }).join('');
    basis.textContent = p && p.basis ? `Basis: ${p.basis}.` : '';
    box.querySelectorAll<HTMLElement>('[data-mtt-slot]').forEach(b => b.addEventListener('click', () => {
      const at = b.dataset.mttSlot!;
      s.picked = at; s.date = at.slice(0, 10); s.time = at.slice(11, 16); s.warnings = [];
      const d = host.querySelector<HTMLInputElement>('[data-mtt-in="date"]'); if (d) d.value = s.date;
      const t = host.querySelector<HTMLInputElement>('[data-mtt-in="time"]'); if (t) t.value = s.time;
      this.paintSlots(); this.paintWarn();
    }));
  }
  private paintWarn(): void {
    const s = this.sheet, host = this.sheetEl;
    if (!s || !host) return;
    const box = host.querySelector<HTMLElement>('[data-mtt-warn]');
    if (!box) return;
    const ws = s.warnings || [];
    box.innerHTML = s.checking
      ? '<div class="mtt-hint">Checking that day…</div>'
      : ws.length ? `<div class="mtt-warnbox" role="status"><b>Heads-up</b><ul>${ws.map((w: any) => `<li>${esc(w.text)}</li>`).join('')}</ul><div class="mtt-hint">You can still book it — the task carries the heads-up.</div></div>` : '';
    const save = host.querySelector<HTMLButtonElement>('[data-mtt="save"]');
    if (save && !s.busy) save.textContent = ws.some((w: any) => w.code !== 'past') ? (s.mode === 'edit' ? 'Save anyway' : 'Schedule anyway') : (s.mode === 'edit' ? 'Save new time' : 'Schedule tour');
  }
  private bindSheet(): void {
    const host = this.sheetEl, s = this.sheet;
    if (!host || !s) return;
    host.querySelector('[data-mtt-sheet]')?.addEventListener('click', (e) => { if ((e.target as HTMLElement).matches('[data-mtt-sheet]') && !s.busy) this.closeSheet(); });
    host.querySelector('[data-mtt-x]')?.addEventListener('click', () => { if (!s.busy) this.closeSheet(); });
    host.querySelector('[data-mtt="cancel"]')?.addEventListener('click', () => { if (!s.busy) this.closeSheet(); });
    host.querySelector('[data-mtt="just"]')?.addEventListener('click', () => {
      if (s.busy) return;
      const id = s.leadId, from = s.from || '';
      this.closeSheet();
      this.h.justMove(id, from);
    });
    host.querySelector('[data-mtt="save"]')?.addEventListener('click', () => { void this.submit(); });
    host.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-mtt-in]').forEach(el => {
      const f = el.dataset.mttIn!;
      const apply = () => {
        if (f === 'advance') { s.advance = (el as HTMLInputElement).checked; return; }
        const v = el.value;
        if (f === 'date' || f === 'time') {
          (s as any)[f] = v;
          s.picked = s.slots && s.slots.some((x: any) => x.at === `${s.date}T${s.time}`) ? `${s.date}T${s.time}` : null;
          this.paintSlots();
          this.queueCheck();
        } else if (f === 'listing') {
          if (v === s.listing) return;
          s.listing = v || null; s.picked = null;
          if (s.mode !== 'edit') { s.date = ''; s.time = ''; }
          const meta = host.querySelector('.mtt-meta'); const l = this.h.lead(s.leadId);
          if (meta && l) meta.textContent = `${l.name} · ${s.listing ? this.h.listingName(s.listing) : 'no home yet'}`;
          void this.loadSlots();
        } else (s as any)[f] = v;
      };
      el.addEventListener('input', apply);
      el.addEventListener('change', apply);
    });
  }
  private setErr(m: string): void {
    const s = this.sheet; if (!s) return;
    s.err = m;
    const box = this.sheetEl?.querySelector<HTMLElement>('[data-mtt-err]');
    if (box) { box.textContent = m; box.hidden = !m; if (m) box.scrollIntoView({ block: 'nearest' }); }
  }
  private setBusy(b: boolean): void {
    const s = this.sheet; if (!s) return;
    s.busy = b;
    this.sheetEl?.querySelectorAll<HTMLButtonElement>('.mtt-foot .btn').forEach(x => { x.disabled = b; });
    const save = this.sheetEl?.querySelector<HTMLButtonElement>('[data-mtt="save"]');
    if (save && b) save.textContent = 'Saving…';
    if (!b) this.paintWarn();
  }

  private async submit(): Promise<void> {
    const s = this.sheet;
    if (!s || s.busy) return;
    const l = this.h.lead(s.leadId);
    if (!l) { this.closeSheet(); return; }
    this.setErr('');
    if (!s.listing) { this.setErr('Pick the home first.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s.date) || !/^\d{2}:\d{2}$/.test(s.time)) { this.setErr('Pick the tour day and time.'); return; }
    const at = `${s.date}T${s.time}`;
    if (s.date < localToday()) { this.setErr('That day has passed — pick today or later.'); return; }
    const body: any = { at, with: s.with.trim() || null, note: s.note.trim() || null };
    if (s.listing !== String(l.property_interest || '') || s.mode === 'edit') body.listing_id = s.listing;
    this.setBusy(true);
    try {
      if (s.mode === 'edit') {
        const r = await this.h.send('PUT', `/leads/${encodeURIComponent(l.id)}/tours/${encodeURIComponent(s.tour.id)}`, body);
        if (!r.ok) { this.setErr(r.j.error || `Couldn’t save the new time (${r.status || 'network'}).`); return; }
        this.done(r.j.lead, r.j, `Tour moved to <b>${esc(whenLabel(at))}</b>`);
        return;
      }
      // a lead with no home yet takes this one (the calendar sheet's rule)
      if (!l.property_interest) {
        const hp = await this.h.send('POST', `/leads/${encodeURIComponent(l.id)}`, { property_interest: s.listing });
        if (!hp.ok) { this.setErr(hp.j.error || 'Couldn’t set the lead’s home.'); return; }
        this.h.applyLead(hp.j);
        delete body.listing_id;
      }
      if (s.mode === 'move') {
        const from = s.from || l.stage;
        if (ADVANCE_FROM.includes(from)) {
          // ONE write: the stage + the tour (expect_stage → 409 when someone moved it first)
          const r = await this.h.send('POST', `/leads/${encodeURIComponent(l.id)}/tours`, { ...body, advance: true, expect_stage: from });
          if (r.status === 409) { await this.h.reloadLeads(); this.setErr(`${r.j.error || 'Someone else moved this lead.'} Nothing was saved — check its stage, then schedule again.`); return; }
          if (!r.ok) { this.setErr(r.j.error || `Couldn’t schedule the tour (${r.status || 'network'}).`); return; }
          this.done(r.j.lead, r.j, `Tour set · <b>${esc(whenLabel(at))}</b> · ${esc(firstName(l.name))} → Touring`);
          return;
        }
        // back into Touring from a later stage: the move, then the tour
        const mv = await this.h.send('POST', `/leads/${encodeURIComponent(l.id)}`, { stage: 'touring', expect_stage: from });
        if (mv.status === 409) { await this.h.reloadLeads(); this.setErr(`${mv.j.error || 'Someone else moved this lead.'} Nothing was saved.`); return; }
        if (!mv.ok) { this.setErr(mv.j.error || `Couldn’t move the lead (${mv.status || 'network'}).`); return; }
        this.h.applyLead(mv.j);
      }
      const r = await this.h.send('POST', `/leads/${encodeURIComponent(l.id)}/tours`, { ...body, advance: s.mode === 'add' ? s.advance : false });
      if (!r.ok) { this.setErr(r.j.error || `Couldn’t schedule the tour (${r.status || 'network'}).`); return; }
      this.done(r.j.lead, r.j, `Tour set · <b>${esc(whenLabel(at))}</b>${r.j.advanced ? ` · ${esc(firstName(l.name))} → Touring` : ''}`);
    } finally {
      if (this.sheet === s) this.setBusy(false);
    }
  }
  private done(view: any, j: any, head: string): void {
    const who = j && j.task && Array.isArray(j.task.assignees) && j.task.assignees.length ? this.personName(j.task.assignees[0]) : '';
    const tail = j && j.task_error ? ' · the task will catch up shortly' : who ? ` · task for <b>${esc(who)}</b>` : j && j.task ? ' · task created' : '';
    const warn = j && Array.isArray(j.warnings) && j.warnings.length ? ` · ⚠ ${esc(j.warnings[0].text)}` : '';
    this.closeSheet();
    this.h.applyLead(view);
    this.h.toast(`${head}${tail}${warn}`);
  }
}

// ── styles (new prefixed block; tokens only) ──────────────────────────────
export const TOURS_CSS = `
  .mtt-cardchip { display:inline-flex; align-items:center; gap:6px; max-width:100%; margin-top:7px; padding:3px 9px; border-radius:var(--radius-pill); font-size:10.5px; font-weight:600; background:var(--accent-soft); color:var(--accent); border:1px solid var(--accent-border); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-variant-numeric:tabular-nums; }
  .mtt-cardchip.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
  button.mtt-cardchip.empty { font:inherit; font-size:10.5px; font-weight:600; cursor:pointer; background:var(--card); color:var(--warning-text); border:1px dashed var(--warning); }
  button.mtt-cardchip.empty:hover { background:var(--warning-soft); }
  .mtt-sec .mtt-note { font-size:12px; line-height:1.45; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning-border); border-radius:var(--radius-sm); padding:8px 10px; margin-bottom:10px; }
  .mtt-tours { display:flex; flex-direction:column; gap:8px; }
  .mtt-tour { border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); padding:9px 11px; }
  .mtt-tour.scheduled { border-color:var(--accent-border); }
  .mtt-tour.cancelled, .mtt-tour.no_show { background:var(--bg-subtle); }
  .mtt-tour-h { display:flex; align-items:center; gap:8px; min-height:30px; }
  .mtt-tour-h .kb-btn { margin-left:auto; color:var(--text-muted); }
  .mtt-when { font-size:13px; font-weight:700; color:var(--text); font-variant-numeric:tabular-nums; }
  .mtt-tour.cancelled .mtt-when { text-decoration:line-through; color:var(--text-muted); }
  .mtt-pill { flex:none; font-size:10.5px; font-weight:600; padding:2px 8px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); white-space:nowrap; }
  .mtt-pill.info { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
  .mtt-pill.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
  .mtt-pill.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
  .mtt-sub { margin-top:2px; font-size:12px; color:var(--text-muted); overflow-wrap:anywhere; }
  .mtt-sub.muted { color:var(--text-faint); }
  .mtt-taskrow { margin-top:6px; }
  .mtt-task { display:inline-flex; align-items:center; gap:4px; max-width:100%; font:inherit; font-size:11.5px; font-weight:600; color:var(--text); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-pill); padding:4px 10px; min-height:28px; cursor:pointer; text-align:left; overflow-wrap:anywhere; }
  .mtt-task:hover { border-color:var(--accent-border); color:var(--accent); }
  .mtt-task.none { cursor:default; font-weight:500; color:var(--text-faint); background:none; border-style:dashed; }
  .mtt-differ { margin-top:8px; padding:8px 10px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); font-size:12px; line-height:1.45; }
  .mtt-differ-acts { display:flex; flex-wrap:wrap; gap:6px; margin-top:7px; }
  .mtt-tour-acts { display:flex; gap:6px; margin-top:8px; }
  .mtt-tour-acts .btn, .mtt-differ-acts .btn { min-height:32px; }
  .mtt-ask { margin-top:8px; padding:8px 10px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); border:1px solid var(--danger-border); font-size:12px; display:flex; flex-wrap:wrap; align-items:center; gap:6px 10px; }
  .mtt-ask-acts { display:inline-flex; gap:6px; margin-left:auto; }
  .mtt-addrow { margin-top:10px; }
  .mtt-wide { flex:1; justify-content:center; }
  .mtt-linkbtn { border:none; background:none; padding:0 0 0 6px; font:inherit; font-size:12px; font-weight:600; color:var(--accent); cursor:pointer; }
  .mtt-linkbtn:hover { text-decoration:underline; }
  .mtt-homeedit { display:flex; flex-direction:column; gap:8px; }
  .mtt-homeq { padding:8px 10px; border-radius:var(--radius-sm); background:var(--bg-subtle); border:1px solid var(--border); display:flex; flex-direction:column; gap:4px; }
  .mtt-k { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); }
  .mtt-radio { display:flex; align-items:center; gap:8px; font-size:12.5px; min-height:30px; cursor:pointer; }
  /* the sheet: a centred card on desktop, a bottom sheet on phones */
  .mtt-ov { position:fixed; inset:0; z-index:9700; background:rgba(15,23,42,.45); display:flex; align-items:center; justify-content:center; padding:24px; }
  .mtt-ov.enter { animation:mttFade .14s ease-out; }
  .mtt-ov.enter .mtt-sheet { animation:mttUp .18s ease-out; }
  @keyframes mttFade { from { background:rgba(15,23,42,0); } }
  @keyframes mttUp { from { transform:translateY(16px); opacity:.6; } }
  .mtt-sheet { width:min(560px, 100%); max-height:min(860px, calc(100vh - 48px)); display:flex; flex-direction:column; background:var(--bg); border-radius:var(--radius); box-shadow:var(--shadow-pop); overflow:hidden; }
  .mtt-head { padding:16px 20px 12px; background:var(--card); border-bottom:1px solid var(--border); }
  .mtt-eyebrow { display:flex; align-items:center; gap:10px; font-size:11.5px; color:var(--text-muted); min-width:0; }
  .mtt-tag { flex:none; font-size:10px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--accent); background:var(--accent-soft); border-radius:var(--radius-pill); padding:2px 8px; }
  .mtt-meta { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .mtt-x { flex:none; margin-left:auto; border:none; background:none; font-size:16px; color:var(--text-muted); cursor:pointer; min-width:36px; height:36px; border-radius:var(--radius-sm); }
  .mtt-x:hover { background:var(--border-soft); color:var(--text); }
  .mtt-title { margin:4px 0 0; font-size:19px; font-weight:700; letter-spacing:-.01em; color:var(--text); }
  .mtt-lede { margin:4px 0 0; font-size:12.5px; line-height:1.5; color:var(--text-muted); }
  .mtt-body { flex:1; overflow-y:auto; overscroll-behavior:contain; padding:14px 20px 18px; display:flex; flex-direction:column; gap:12px; }
  .mtt-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); padding:12px 14px; display:flex; flex-direction:column; gap:10px; }
  .mtt-label { display:flex; align-items:center; gap:8px; font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.09em; color:var(--text-faint); }
  .mtt-cnt { font-size:10.5px; font-weight:700; color:var(--text-muted); background:var(--bg-subtle); border:1px solid var(--border); border-radius:var(--radius-pill); padding:0 7px; letter-spacing:0; font-variant-numeric:tabular-nums; }
  .mtt-slots { display:flex; flex-direction:column; gap:7px; }
  .mtt-slot { display:flex; align-items:stretch; gap:12px; width:100%; text-align:left; font:inherit; color:var(--text); background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:9px 11px; cursor:pointer; min-height:52px; transition:border-color .12s ease, background .12s ease, box-shadow .12s ease; }
  .mtt-slot:hover { border-color:var(--accent-border); }
  .mtt-slot:focus-visible { outline:none; box-shadow:var(--focus-ring); border-color:var(--accent); }
  .mtt-slot.on { border-color:var(--accent); background:var(--accent-soft); box-shadow:0 0 0 1px var(--accent) inset; }
  .mtt-slot-when { flex:none; width:92px; display:flex; flex-direction:column; justify-content:center; gap:1px; }
  .mtt-slot-when .d { font-size:13px; font-weight:700; }
  .mtt-slot-when .t { font-size:12px; color:var(--text-muted); font-variant-numeric:tabular-nums; }
  .mtt-slot.on .mtt-slot-when .t { color:var(--accent); font-weight:600; }
  .mtt-slot-body { flex:1; min-width:0; display:flex; flex-direction:column; align-items:flex-start; gap:3px; }
  .mtt-kind { font-size:10px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; padding:1px 7px; border-radius:var(--radius-pill); border:1px solid var(--border); background:var(--bg-subtle); color:var(--text-muted); }
  .mtt-kind.k-turnover { background:var(--accent-soft); color:var(--accent); border-color:var(--accent-border); }
  .mtt-kind.k-vacant { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border); }
  .mtt-kind.k-arrival_morning { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border); }
  .mtt-why { font-size:11.5px; line-height:1.4; color:var(--text-muted); overflow-wrap:anywhere; }
  .mtt-also { font-size:11px; color:var(--info-text); }
  .mtt-slot-tick { flex:none; width:18px; align-self:center; color:var(--accent); font-weight:700; text-align:center; }
  .mtt-skel { height:52px; border-radius:var(--radius-sm); background:linear-gradient(90deg, var(--bg-subtle), var(--border-soft), var(--bg-subtle)); background-size:200% 100%; animation:mttSkel 1.1s linear infinite; }
  @keyframes mttSkel { from { background-position:200% 0; } to { background-position:-200% 0; } }
  .mtt-empty { font-size:12.5px; line-height:1.5; color:var(--text-muted); padding:8px 10px; border:1px dashed var(--border-strong); border-radius:var(--radius-sm); }
  .mtt-empty-sub { margin-top:3px; font-size:11.5px; color:var(--text-faint); }
  .mtt-basis { font-size:11px; line-height:1.45; color:var(--text-faint); }
  .mtt-basis:empty { display:none; }
  .mtt-banner { font-size:12.5px; line-height:1.5; padding:9px 12px; border-radius:var(--radius-sm); }
  .mtt-banner.warn { background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); }
  .mtt-two { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:10px; }
  .mtt-f { display:flex; flex-direction:column; gap:5px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); min-width:0; }
  .mtt-input { font:inherit; font-size:13px; text-transform:none; letter-spacing:0; font-weight:500; color:var(--text); background:var(--card); border:1px solid var(--border); border-radius:var(--radius-btn); padding:8px 10px; min-height:38px; box-sizing:border-box; width:100%; min-width:0; }
  .mtt-input:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }
  .mtt-hint { font-size:11px; font-weight:400; text-transform:none; letter-spacing:0; color:var(--text-faint); line-height:1.4; }
  .mtt-hint-inline { font-weight:500; text-transform:none; letter-spacing:0; color:var(--text-faint); }
  .mtt-check { display:flex; align-items:center; gap:8px; font-size:12.5px; color:var(--text); min-height:32px; cursor:pointer; }
  .mtt-warnbox { font-size:12px; line-height:1.45; padding:8px 11px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); }
  .mtt-warnbox ul { margin:4px 0 4px; padding-left:18px; }
  .mtt-err { font-size:12.5px; line-height:1.45; padding:9px 12px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); border:1px solid var(--danger-border); }
  .mtt-err[hidden] { display:none; }
  .mtt-foot { display:flex; justify-content:flex-end; align-items:center; gap:8px; padding:12px 20px; background:var(--card); border-top:1px solid var(--border); }
  .mtt-foot .btn { min-height:40px; }
  @media (max-width: 640px) {
    .mtt-ov { padding:0; align-items:flex-end; }
    .mtt-sheet { width:100%; max-height:92vh; max-height:92dvh; border-radius:var(--radius) var(--radius) 0 0; }
    .mtt-ov.enter .mtt-sheet { animation:mttUpPhone .2s ease-out; }
    @keyframes mttUpPhone { from { transform:translateY(40px); } }
    .mtt-head { padding:12px 16px 10px; }
    .mtt-title { font-size:17px; }
    .mtt-body { padding:12px 12px 16px; }
    .mtt-card { padding:12px; }
    .mtt-input { font-size:16px; min-height:42px; }
    .mtt-slot-when { width:84px; }
    .mtt-foot { display:grid; grid-template-columns:1fr; padding:10px 12px calc(10px + env(safe-area-inset-bottom)); }
    .mtt-foot.two { grid-template-columns:minmax(0,1fr) minmax(0,1.6fr); }
    .mtt-foot .btn { width:100%; justify-content:center; min-height:44px; }
    .mtt-foot:not(.two) [data-mtt="cancel"] { display:none; }
    .mtt-tour-acts .btn, .mtt-differ-acts .btn, .mtt-task { min-height:36px; }
    .mtt-tour-h .kb-btn { min-width:36px; height:36px; }
    button.mtt-cardchip.empty { min-height:36px; font-size:11.5px; }
    .mtt-linkbtn { min-height:36px; padding-left:8px; }
  }
`;
